import { useState } from "react";
import NewsModal, { NewsDetail } from "./NewsModal";

const newsItems: NewsDetail[] = [
  {
    date: "January 15, 2025",
    category: "Company News",
    title: "New Logistics Center Opens in Moncton",
    description:
      "We are excited to announce the opening of a state-of-the-art distribution center spanning 25,000 sq.m with advanced cold storage systems",
    image:
      "https://public.readdy.ai/ai/img_res/d7bdd4b8cac23515b5b8ee550861cb9d.jpg",
    fullContent:
      "McAllister Delight is proud to announce the grand opening of its newest and most advanced distribution hub in the Greater Moncton Area. This 25,000 sq.m facility represents our largest single infrastructure investment to date, designed to meet the growing demand for cold chain logistics across New Brunswick and beyond. Equipped with cutting-edge automation, real-time inventory tracking, and multi-zone temperature control, the center will serve as the cornerstone of our eastern Canada operations. The facility is fully HACCP and SQF-certified and built to LEED sustainability standards, reflecting our commitment to both food safety and environmental responsibility.",
    stats: [
      { icon: "ri-building-2-line", label: "Facility Size", value: "25,000 sq.m" },
      { icon: "ri-snowflake-line", label: "Temperature Zones", value: "5 Controlled Zones" },
      { icon: "ri-user-line", label: "New Jobs Created", value: "200+ Positions" },
      { icon: "ri-truck-line", label: "Daily Truck Capacity", value: "180+ Vehicles" },
    ],
    bulletPoints: [
      "State-of-the-art automated conveyor and sorting systems for faster throughput",
      "Multi-zone refrigeration: -25°C frozen to +15°C produce and dairy",
      "LEED Silver certified building with solar panels and green roof",
      "Integrated real-time WMS with client portal access",
      "Dedicated cross-docking bay with 32 loading doors",
      "Backup power generation ensuring 24/7 cold chain integrity",
    ],
  },
  {
    date: "January 8, 2025",
    category: "Certification",
    title: "ISO 22000 Food Safety Certification Achieved",
    description:
      "Our company has successfully obtained international certification confirming the highest standards of quality in food safety management",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20food%20safety%20auditor%20in%20white%20coat%20inspecting%20modern%20cold%20storage%20warehouse%20facility%20with%20clipboard%20and%20digital%20tablet%20checking%20temperature%20logs%20quality%20control%20inspection%20ISO%2022000%20certification%20process%20industrial%20refrigeration%20units%20in%20background%20clean%20organized%20environment%20professional%20corporate%20photography&width=800&height=500&seq=news-iso-audit-003&orientation=landscape",
    fullContent:
      "McAllister Delight has successfully achieved ISO 22000:2018 Food Safety Management System certification, marking a landmark milestone in our commitment to delivering the highest standards of food safety across our entire cold chain network. The certification, awarded by Bureau Veritas following a rigorous multi-site audit spanning our Moncton, Saint John, and Fredericton operations, validates our integrated food safety protocols from point of origin to final delivery. This achievement positions McAllister Delight among a select group of Canadian logistics providers holding full ISO 22000 compliance, reinforcing our position as a trusted partner for food manufacturers, retailers, and distributors.",
    stats: [
      { icon: "ri-award-line", label: "Standard", value: "ISO 22000:2018" },
      { icon: "ri-global-line", label: "Auditing Body", value: "Bureau Veritas" },
      { icon: "ri-map-pin-line", label: "Sites Certified", value: "3 Facilities" },
      { icon: "ri-calendar-check-line", label: "Valid Until", value: "January 2028" },
    ],
    bulletPoints: [
      "Full compliance with Codex Alimentarius and FSMA regulatory requirements",
      "Hazard Analysis and Critical Control Points (HACCP) fully integrated",
      "Annual third-party surveillance audits scheduled across all sites",
      "Staff training programs updated to reflect ISO 22000 protocols",
      "Supplier evaluation framework enhanced under new certification criteria",
      "Digital food safety documentation system implemented chain-wide",
    ],
  },
  {
    date: "December 22, 2024",
    category: "Fleet & Operations",
    title: "Fleet Expansion: 50 New Refrigerated Trucks",
    description:
      "Investment in fleet renewal will increase transport capacity by 40% and reduce delivery times on key routes across Canada",
    image:
      "https://readdy.ai/api/search-image?query=Multiple%20American%20semi%20trucks%20Kenworth%20Peterbilt%20Freightliner%20long-nose%20cabover%20white%20refrigerated%20trailers%20lined%20up%20in%20large%20Canadian%20logistics%20depot%20yard%20daytime%20clear%20sky%20flat%20asphalt%20clean%20industrial%20background%20professional%20commercial%20fleet%20photography%20North%20American%20style%20trucks%20no%20European%20trucks&width=800&height=500&seq=news-fleet-american-009&orientation=landscape",
    fullContent:
      "McAllister Delight has completed the acquisition and deployment of 50 new Carrier Transicold-equipped refrigerated trailers, representing a $14M investment in fleet modernization. The new units join our existing roster of 120 vehicles, expanding total transport capacity by 40% and enabling faster, more reliable delivery on high-volume corridors including Toronto–Montreal, Calgary–Vancouver, and Ottawa–Halifax. Each truck is fitted with GPS telematics, automated temperature logging, and fuel-efficient Euro VI engines, reducing our fleet's carbon footprint by an estimated 22% per kilometre. The expansion directly responds to increased demand from our grocery retail and foodservice clients heading into the 2025 peak season.",
    stats: [
      { icon: "ri-truck-line", label: "New Vehicles", value: "50 Refrigerated Trailers" },
      { icon: "ri-funds-line", label: "Investment", value: "$14 Million CAD" },
      { icon: "ri-arrow-up-line", label: "Capacity Increase", value: "+40%" },
      { icon: "ri-leaf-line", label: "Emissions Reduction", value: "22% per km" },
    ],
    bulletPoints: [
      "Carrier Transicold multi-temperature refrigeration units on every trailer",
      "Live GPS tracking and automated temperature reporting via client portal",
      "Euro VI low-emission engines meeting all 2025 Canadian fleet standards",
      "Expanded coverage on 6 major cross-Canada freight corridors",
      "Dedicated lanes for pharmaceutical and specialty food transport",
      "24/7 roadside assistance network integrated into all new units",
    ],
  },
];

export default function News() {
  const [selectedNews, setSelectedNews] = useState<NewsDetail | null>(null);

  return (
    <section id="news" className="py-16 md:py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-12">
        <div className="mb-10 md:mb-16">
          <p className="text-[#DC2626] text-xs md:text-sm font-bold uppercase tracking-wider mb-3 md:mb-4">
            NEWS &amp; UPDATES
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A365D]">Latest Events</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-8">
          {newsItems.map((item) => (
            <article
              key={item.title}
              className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              onClick={() => setSelectedNews(item)}
            >
              <div className="h-48 md:h-56 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  title={item.title}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="p-5 md:p-8">
                <div className="flex items-center space-x-2 text-gray-500 text-xs md:text-sm mb-3 md:mb-4">
                  <i className="ri-calendar-line"></i>
                  <time>{item.date}</time>
                </div>

                <h3 className="text-lg md:text-xl font-bold text-[#1A365D] mb-2 md:mb-3 line-clamp-2 leading-snug">
                  {item.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-4 md:mb-5 line-clamp-3">
                  {item.description}
                </p>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNews(item);
                  }}
                  className="text-[#DC2626] font-semibold text-sm hover:text-[#B91C1C] transition-colors inline-flex items-center space-x-1 whitespace-nowrap cursor-pointer"
                >
                  <span>Read More</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      <NewsModal news={selectedNews} onClose={() => setSelectedNews(null)} />
    </section>
  );
}
